import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ArrowRight, Star } from "lucide-react";
import { getObiReview, getProducts, formatPrice } from "../lib/api";
import { OBI, productImage } from "../assets/obi";
import { useLang } from "../i18n";

const CARD_COLORS = [
  { bg: "bg-pink-200", accent: "bg-pink-400" },
  { bg: "bg-sky-200", accent: "bg-sky-400" },
  { bg: "bg-yellow-200", accent: "bg-yellow-400" },
];

const ObiPickCard = ({ product, lang, color, idx }) => {
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const { t } = useLang();

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getObiReview(product.id, lang)
      .then((r) => {
        if (alive) setReview(r.review);
      })
      .catch(() => {
        if (alive) setReview(null);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [product.id, lang]);

  return (
    <article
      data-testid={`obi-pick-${product.id}`}
      className={`cartoon-card ${color.bg} p-6 flex flex-col gap-4`}
    >
      <div className="flex items-start gap-4">
        <Link to={`/product/${product.id}`} className="shrink-0">
          <div className="w-24 h-24 bg-white border-4 border-slate-900 rounded-2xl overflow-hidden cartoon-shadow-sm">
            <img
              src={productImage(product, idx)}
              alt={product.name}
              className="w-full h-full object-cover"
            />
          </div>
        </Link>
        <div className="flex-1 min-w-0">
          <span className={`inline-flex items-center gap-1.5 ${color.accent} border-2 border-slate-900 rounded-full px-2.5 py-0.5 text-xs font-bold mb-1`}>
            <img src={OBI.dice} alt="" className="w-4 h-4 object-cover rounded-full" />
            {t("home.obi_badge")}
          </span>
          <h3 className="font-display font-bold text-lg leading-tight line-clamp-2">
            <Link to={`/product/${product.id}`} className="hover:text-pink-600 transition-colors">
              {product.name}
            </Link>
          </h3>
          <p className="font-display font-bold text-slate-900 mt-1">
            {formatPrice(product.price)}
          </p>
        </div>
      </div>

      <div className="relative bg-white border-4 border-slate-900 rounded-2xl p-4 cartoon-shadow-sm">
        <span className="absolute -top-3 left-4 bg-yellow-300 border-2 border-slate-900 rounded-full px-2 py-0.5 text-xs font-bold flex items-center gap-1">
          <Star className="w-3 h-3" fill="#0F172A" /> OBI
        </span>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-600 py-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            {t("home.obi_loading")}
          </div>
        ) : (
          <p className="text-sm leading-relaxed text-slate-800 italic">
            “{review}”
          </p>
        )}
      </div>

      <Link
        to={`/product/${product.id}`}
        data-testid={`obi-pick-cta-${product.id}`}
        className="cartoon-btn bg-white hover:bg-amber-100 text-sm py-2.5 self-start"
      >
        {t("home.cta_shop")} <ArrowRight className="w-4 h-4" strokeWidth={3} />
      </Link>
    </article>
  );
};

const ObiPicksSection = () => {
  const { t, lang } = useLang();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getProducts({ per_page: 3, orderby: "popularity" })
      .then(setProducts)
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;

  return (
    <section
      data-testid="obi-picks-section"
      className="py-16 lg:py-24 bg-amber-100 border-y-4 border-slate-900"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
          <div className="flex items-end gap-4">
            <img
              src={OBI.dice}
              alt="OBI"
              className="w-28 h-28 lg:w-36 lg:h-36 object-contain animate-float shrink-0"
            />
            <div>
              <span className="inline-flex items-center gap-2 bg-pink-300 border-4 border-slate-900 rounded-full px-3 py-1 font-bold text-sm cartoon-shadow-sm">
                ★ OBI'S PICKS ★
              </span>
              <h2 className="font-display font-bold text-4xl lg:text-5xl text-slate-900 mt-3">
                {t("home.obi_title")}
              </h2>
              <p className="text-slate-700 mt-2 max-w-xl">
                {t("home.obi_subtitle")}
              </p>
            </div>
          </div>
        </div>

        {products.length === 0 ? (
          <div className="cartoon-card p-10 text-center max-w-xl mx-auto bg-white">
            <img src={OBI.empty} alt="OBI" className="w-40 h-40 mx-auto" />
            <p className="font-display font-bold text-xl mt-4">
              {lang === "it"
                ? "OBI sta annusando i prossimi giochi da consigliarti!"
                : "OBI is sniffing the next games to recommend!"}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {products.map((p, i) => (
              <ObiPickCard
                key={p.id}
                product={p}
                lang={lang}
                color={CARD_COLORS[i % CARD_COLORS.length]}
                idx={i}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default ObiPicksSection;
